import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useTranslation } from 'react-i18next';
import { 
  Button, 
  Input, 
  Table, 
  Modal, 
  message, 
  Spin, 
  Tag, 
  Space, 
  Form, 
  Select, 
  InputNumber,
  Divider,
  Card,
  Tooltip,
  Row,
  Col,
  Progress,
  DatePicker
} from 'antd';
import { 
  DeleteOutlined, 
  EditOutlined, 
  ExclamationCircleOutlined,
  ScheduleOutlined,
  BookOutlined,
  UserOutlined,
  DollarOutlined,
  PrinterOutlined,
  GlobalOutlined,
  HomeOutlined,
  VideoCameraOutlined,
  QuestionOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import ScheduleModal from './ScheduleModal';
import './Classes.css';
import dayjs from 'dayjs';

const { confirm } = Modal;
const { Option } = Select;

const FilterTaskTable = () => {
  const { t } = useTranslation();
  const [classGroups, setClassGroups] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [classChangeLogs, setClassChangeLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [popupData, setPopupData] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editData, setEditData] = useState(null);
  const [platformModal, setPlatformModal] = useState(null);
  const [registrationsCount, setRegistrationsCount] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [form] = Form.useForm();
  const [monthlyStats, setMonthlyStats] = useState([]);

  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());

  const itemsPerPage = 50;
  const API_URL = process.env.REACT_APP_API_URL;

  // Your existing utility functions...
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return 'Invalid Date';
  
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit'
    }).format(date);
  };
  
  const formatMinutesToHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}.${remainingMinutes.toString().padStart(2, '0')} ${t('hrs')}`;
  };

  const processClassGroups = (data) => {
    const groupedData = data.reduce((acc, item) => {
      const existing = acc.find((entry) => entry.groupid === item.groupid);
  
      if (existing) {
        existing.price = existing.price
          ? `${existing.price}, ${item.price || ''}`.trim()
          : item.price;
        existing.classgroup_ids = existing.classgroup_ids
          ? `${existing.classgroup_ids}, ${item.classgroup_id}`
          : item.classgroup_id;
      } else {
        acc.push({
          ...item,
          classgroup_ids: item.classgroup_id,
        });
      }
  
      return acc;
    }, []);
  
    return groupedData;
  };

  // New function to fetch monthly statistics
  const fetchMonthlyStats = useCallback(async (month, year) => {
    setStatsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/class-groups/monthly-stats`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { month, year }
      });
      
      setMonthlyStats(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching monthly statistics:', error);
      message.error(t('Error fetching statistics'));
    } finally {
      setStatsLoading(false);
    }
  }, [API_URL, t]);

  const fetchSalespersons = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/sales-persons`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setSalespersons(response.data);
  } catch (error) {
    console.error('Error fetching salespersons:', error);
    message.error(t('Failed to fetch salespersons'));
  }
}, [API_URL, t]);
  
  const fetchClassGroups = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
  
      const response = await axios.get(`${API_URL}/class-groups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      const processedData = processClassGroups(response.data);
      setClassGroups(processedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching class groups:', error);
      setError(t('fetchClassGroupsError'));
      setLoading(false);
    }
  }, [API_URL, t]);
  
  const fetchRegistrationsCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/registrations/registrations-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRegistrationsCount(response.data);
    } catch (error) {
      console.error('Error fetching registrations count:', error);
      setError(t('fetchRegistrationsCountError'));
    }
  }, [API_URL, t]);

  const fetchClassChangeLogs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/class-change/class-change-logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      setClassChangeLogs(response.data);
  
    } catch (error) {
      console.error('Error fetching class change logs:', error);
    }
  }, [API_URL]);

useEffect(() => {
  fetchClassGroups();
  fetchRegistrationsCount();
  fetchClassChangeLogs();
  fetchSalespersons(); 

  const now = new Date();
  fetchMonthlyStats(now.getMonth() + 1, now.getFullYear());
}, [fetchClassGroups, fetchRegistrationsCount, fetchClassChangeLogs, fetchMonthlyStats, fetchSalespersons]);

useEffect(() => {
  if (editModalVisible) {
    fetchSalespersons();
  }
}, [editModalVisible, fetchSalespersons]);


  // Handle month selection change
  const handleMonthChange = (date) => {
    setSelectedMonth(date);
    if (date) {
      fetchMonthlyStats(date.month() + 1, date.year());
    }
  };

  // Action handlers (keeping existing functions)
  const confirmDeleteClassGroup = (groupID) => {
    confirm({
      title: t('Confirm Delete Entire Course'),
      icon: <ExclamationCircleOutlined />,
      content: t('Are you sure you want to delete this course? This action is irreversible and will permanently remove all related data.'),
      okText: t('yes'),
      okType: 'danger',
      cancelText: t('no'),
      onOk() {
        handleDeleteClassGroup(groupID);
      },
      onCancel() {
      },
    });
  };

  const handleDeleteClassGroup = async (groupID) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/class-groups/${groupID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClassGroups((prevClasses) => prevClasses.filter((cls) => cls.groupid !== groupID));
      message.success(t('Class Group Deleted'));
    } catch (error) {
      console.error('Error deleting class group:', error);
      message.error(t('deleteClassGroupError'));
    }
  };

const handleEditClick = (classGroup) => {
  const registrationCount = getRegisteredStudents(classGroup.groupid);

  const formData = {
    groupID: classGroup.groupid,
    class_code: classGroup.class_code,
    book_details_materials: classGroup.book_details_materials,
    isprivate: classGroup.isprivate.toString(),
    register_capacity: classGroup.register_capacity,
    price: classGroup.price,
    salesperson_id: classGroup.salesperson_id || null, // Only need the ID
  };

  form.resetFields();              
  form.setFieldsValue(formData);   
  setEditData({ 
    ...formData, 
    hasRegistrations: registrationCount > 0 
  });
  setEditModalVisible(true);
};


const showPlatformSelection = (groupID) => {
  const modal = Modal.confirm({
    title: t('Select New Platform'),
    icon: null,
    content: (
      <div>
        <Button 
          block 
          onClick={() => updatePlatform(groupID, 'online', modal)}
        >
          <VideoCameraOutlined /> {t('online')}
        </Button>

        <Button 
          block 
          style={{ marginTop: 8 }} 
          onClick={() => updatePlatform(groupID, 'onsite', modal)}
        >
          <GlobalOutlined /> {t('onsite')}
        </Button>

        <Button 
          block 
          style={{ marginTop: 8 }} 
          onClick={() => updatePlatform(groupID, 'athome', modal)}
        >
          <HomeOutlined /> {t('athome')}
        </Button>
      </div>
    ),
    okButtonProps: { style: { display: 'none' } }  // hide OK button
  });
};


// Perform API update
const updatePlatform = async (groupID, platform) => {
  try {
    const token = localStorage.getItem('token');
    await axios.put(
      `${API_URL}/class-groups/update-platform/${groupID}`,
      { platform },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    message.success(t('platformUpdated'));
    fetchClassGroups(); // Refresh

  } catch (err) {
    console.error(err);
    message.error(t('platformUpdateFailed'));
  }
};



const handleSaveEdit = async () => {
  try {
    const values = await form.validateFields();
    const token = localStorage.getItem('token');
    
    const dataToSubmit = {
      ...values,
      isprivate: values.isprivate === 'true',
      groupID: editData.groupID,
      salesperson_id: values.salesperson_id || null, // Explicitly handle salesperson_id
    };
  
    
    await axios.put(`${API_URL}/class-groups/${editData.groupID}`, dataToSubmit, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setEditModalVisible(false);
    form.resetFields();
    fetchClassGroups();
    fetchRegistrationsCount();
    fetchClassChangeLogs();

    if (selectedMonth) {
      fetchMonthlyStats(selectedMonth.month() + 1, selectedMonth.year());
    } else {
      const now = new Date();
      fetchMonthlyStats(now.getMonth() + 1, now.getFullYear());
    }

    message.success(t('classGroupUpdated'));
  } catch (error) {
    console.error('Error updating class group:', error);
    message.error(t('updateClassGroupError'));
  }
};
  
  const getRegisteredStudents = (groupId) => {
    const group = registrationsCount.find((rc) => rc.groupid === groupId);
    return group ? group.registered_students : 0;
  };

  const formatNumber = (number) => {
    if (!number) return "0.00";
    return parseFloat(number).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const filteredClassGroups = classGroups.filter((group) =>
    group.class_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleRefresh = () => {
    fetchClassGroups();
    fetchRegistrationsCount();
    fetchClassChangeLogs();
    // Refresh current month stats
    fetchMonthlyStats(selectedMonth.month() + 1, selectedMonth.year());
  };

  const columns = [
    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => {
        const totalRegistrations = getRegisteredStudents(record.groupid);
        const hasRegistrations = totalRegistrations > 0;
        
        return (
          <Space>
            <Tooltip title={t('edit')}>
              <Button 
                type="primary"
                shape="circle"
                icon={<EditOutlined />} 
                onClick={() => handleEditClick(record)} 
                ghost
              />
            </Tooltip>
            <Tooltip title={hasRegistrations ? t('Cannot Delete With Registrations') : t('delete')}>
              <Button 
                danger
                type="primary"
                shape="circle"
                icon={<DeleteOutlined />} 
                onClick={() => hasRegistrations ? null : confirmDeleteClassGroup(record.groupid)}
                disabled={hasRegistrations}
              />
            </Tooltip>
          </Space>
        );
      }
    },
{
  title: t('sales/date'),
  key: 'sales_date',
  sorter: (a, b) => new Date(a.create_date) - new Date(b.create_date),
  defaultSortOrder: 'descend',
  render: (_, record) => (
    <div>
      <div style={{ fontSize: '12px' }}>
        {record.salesperson_nickname ? (
          <Tag color="blue">{record.salesperson_nickname}</Tag>
        ) : (
          <Tag color="default">{t('No Sales')}</Tag>
        )}
      </div>
      <div style={{ fontSize: '12px', color: '#888' }}>
        ({record.create_date ? formatDate(record.create_date) : ''})
      </div>
    </div>
  ),
},

    {
      title: t('classCode'),
      dataIndex: 'class_code',
      key: 'class_code',
      width: 250,
      render: (text, record) => (
        <Link
          to={`/classdetails/${record.groupid}`}
          style={{
            fontWeight: 'bold',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          {text}
        </Link>
      ),
    },
    {
      title: t('print'),
      key: 'schedule',
      render: (_, record) => (
        <Button 
          icon={<PrinterOutlined />}
          onClick={() => {
            const classesWithPlatform = record.classes?.map(classItem => ({
              ...classItem,
              platform: classItem.platform, 
              isprivate: record.isprivate,
              salesperson_nickname: record.salesperson_nickname,  // NEW
              salesperson_phone: record.salesperson_phone,        // NEW
              salesperson_email: record.salesperson_email         // NEW
            })) || [];
            
            setPopupData(classesWithPlatform);
          }}
        >
        </Button>
      ),
    },
     {
  title: t('schedule'),
  key: 'schedule',
  render: (_, record) => (
    <Link to={`/course/${record.groupid}`}>
      <Button 
        icon={<ScheduleOutlined />}
        type="default"
        style={{ width: '50px' }}
      >
      </Button>
    </Link>
  ),
},
   {
  title: t('tutor'),
  key: 'teachers',
  width: 250,
  render: (_, record) => {
    const teacherNicknames = [...new Set(
      (record.classes || [])
        .map(cls => cls.teacher_nickname || cls.nickname) // ensure it works with teacher_nickname
        .filter(Boolean)
    )];

    if (teacherNicknames.length === 0) return '-';

    return (
      <span
        style={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }}
      >
        {teacherNicknames.join(', ')}
      </span>
    );
  }
},

    {
      title: t('startDate'),
      dataIndex: 'start_date',
      key: 'start_date',
      render: formatDate,
      sorter: (a, b) => new Date(a.start_date) - new Date(b.start_date),
      defaultSortOrder: 'descend', 
    },    
    { title: t('endDate'), dataIndex: 'end_date', key: 'end_date', render: formatDate },
    { title: t('totalHours'), dataIndex: 'total_hours', key: 'total_hours', render: formatMinutesToHours },
    {
      title: t('note'),
      dataIndex: 'book_details_materials',
      key: 'book_details_materials',
      width: 250, 
      render: (text) => (
        <div style={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        }}>
          {text}
        </div>
      ),
    },
    {
      title: t('private'),
      dataIndex: 'isprivate',
      key: 'isprivate',
      render: (isPrivate) => (
        <Tag color={isPrivate ? 'blue' : 'green'}>
          {isPrivate ? t('private') : t('group')}
        </Tag>
      ),
    },
{
  title: t('platform'),
  key: 'platform',
  render: (_, record) => {
    const platforms = record.platforms || [];

    const getPlatformConfig = (platform) => {
      switch (platform) {
        case 'online':
          return { text: t('online'), icon: <VideoCameraOutlined />, color: 'blue' };
        case 'onsite':
          return { text: t('onsite'), icon: <GlobalOutlined />, color: 'green' };
        case 'athome':
          return { text: t('athome'), icon: <HomeOutlined />, color: 'orange' };
        default:
          return { text: platform || '-', icon: <QuestionOutlined />, color: 'default' };
      }
    };

    const handleChangePlatform = () => {
      const hasMultiple = platforms.length > 1;

      Modal.confirm({
        title: t('Confirm Platform Change'),
        icon: <ExclamationCircleOutlined />,
        content: hasMultiple
          ? t('This course has multiple platforms set up for individual classes. Updating will overwrite ALL classes to the same platform. Continue?')
          : t('This will update the platform for ALL classes in this course. Continue?'),
        okText: t('yes'),
        cancelText: t('no'),
        onOk: () => showPlatformSelection(record.groupid)
      });
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        
        {/* Platform tags */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {platforms.length === 0 && <Tag>{t('noPlatform')}</Tag>}
          {platforms.map((p) => {
            const cfg = getPlatformConfig(p);
            return (
              <Tag key={p} color={cfg.color}>
                {cfg.icon} {cfg.text}
              </Tag>
            );
          })}
        </div>

        {/* Edit icon */}
        <Button 
          type="text"
          icon={<EditOutlined />}
          onClick={handleChangePlatform}
          style={{ padding: '4px' }}
        />
      </div>
    );
  }
},
    { title: t('capacity'), dataIndex: 'register_capacity', key: 'register_capacity' },
    {
      title: t('Reg'),
      key: 'totalRegis',
      render: (_, record) => getRegisteredStudents(record.groupid),
    },

    {
      title: t('price'),
      key: 'price',
      render: (_, record) => {
        const hasBasePrice = record.price && record.price !== "0.00";
        const hasPackages = record.package_pricing && record.package_pricing.length > 0;
    
        return (
          <div>
            {hasBasePrice && (
              <div style={{ marginBottom: hasPackages ? '12px' : '0' }}>
                <span style={{ 
                  color: '#1890ff',
                  fontWeight: 500,
                  fontSize: '15px'
                }}>
                  {formatNumber(record.price)} {t('THB')}
                </span>
              </div>
            )}
            
            {hasBasePrice && hasPackages && (
              <div style={{
                margin: '8px 0',
                borderBottom: '1px dashed #d9d9d9',
                width: '100%'
              }} />
            )}
            
            {hasPackages && (
              <ul style={{ 
                padding: 0, 
                margin: 0, 
                listStyleType: 'none',
                color: '#52c41a'
              }}>
                {record.package_pricing.map((pkg, index) => (
                  <li 
                    key={`${record.groupid}-${index}`} 
                    style={{ 
                      marginBottom: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <strong style={{ color: '#262626' }}>{pkg.number_of_classes} {t('classes')}:</strong>{' '}
                    <span style={{ fontWeight: 500 }}>{formatNumber(pkg.price_per_package)} {t('THB')}</span>{' '}
                    <span style={{ color: '#8c8c8c' }}>({pkg.validity_months} {t('months')})</span>
                  </li>
                ))}
              </ul>
            )}
            
            {(!hasBasePrice && !hasPackages) && (
              <span style={{ color: '#bfbfbf', fontStyle: 'italic' }}>
                {t('noPriceAvailable')}
              </span>
            )}
          </div>
        );
      },
    },
    { title: t('created by'), dataIndex: 'admin_nickname', key: 'admin_nickname' },
  ];
  
  return (
    <div>
      <Sidebar />
      <Header />
      <div className="manage-course-container">
        {/* Monthly Statistics Section */}
        
          <Card 
  title={
    <Space>
      <TrophyOutlined />
      {t('Monthly Statistics by Salesperson')}
    </Space>
  }
  style={{ marginBottom: '20px' }}
  extra={
    <Space>
      <DatePicker
        picker="month"
        value={selectedMonth}
        onChange={handleMonthChange}
        format="MMMM YYYY"
        allowClear={false}
      />
    </Space>
  }
>
  <Spin spinning={statsLoading}>
    <Row gutter={[16, 0]}>
      {monthlyStats.map((stat, idx) => (
        <Col key={idx} xs={24} sm={12} md={8}>
          <div style={{ 
            border: '1px solid #f0f0f0', 
            borderRadius: '8px', 
            padding: '16px',
            height: '100%',
            background: '#fafafa'
          }}>
            {/* Salesperson Name */}
            <div style={{ 
              fontWeight: 'bold', 
              fontSize: '16px', 
              marginBottom: '12px',
              textAlign: 'center',
              borderBottom: '1px solid #e8e8e8',
              paddingBottom: '8px'
            }}>
              {stat.salesperson.nickname || stat.salesperson.name || t('No Sales')}
            </div>

            {/* 2x2 Grid Layout */}
            <Row gutter={[8, 8]} style={{ marginBottom: '12px' }}>
              {/* Active Courses - Top Left */}
              <Col span={12}>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ color: '#722ed1', fontSize: '16px', fontWeight: 'bold' }}>
                    <BookOutlined style={{ marginRight: '4px' }} />
                    {stat.activeCourses}
                  </div>
                  <div style={{ fontSize: '10px', color: '#666' }}>
                    {t('Active Courses')} ({stat.totalClasses})
                  </div>
                </div>
              </Col>

              {/* Total Hours - Top Right */}
              <Col span={12}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#1890ff', fontSize: '16px', fontWeight: 'bold' }}>
                    <ClockCircleOutlined style={{ marginRight: '4px' }} />
                    {stat.totalHours}
                  </div>
                  <div style={{ fontSize: '10px', color: '#666' }}>{t('Total Hours')}</div>
                </div>
              </Col>

              {/* Hours Taught - Bottom Left */}
              <Col span={12}>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ color: '#52c41a', fontSize: '16px', fontWeight: 'bold' }}>
                    <CheckCircleOutlined style={{ marginRight: '4px' }} />
                    {stat.taughtHours}
                  </div>
                  <div style={{ fontSize: '10px', color: '#666' }}>
                    {t('Taught Hours')} ({stat.percentages.taught}%)
                  </div>
                </div>
              </Col>

              {/* Hours Remaining - Bottom Right */}
              <Col span={12}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#fa8c16', fontSize: '16px', fontWeight: 'bold' }}>
                    <CalendarOutlined style={{ marginRight: '4px' }} />
                    {stat.remainingHours}
                  </div>
                  <div style={{ fontSize: '10px', color: '#666' }}>
                    {t('Remaining Hours')} ({stat.percentages.remaining}%)
                  </div>
                </div>
              </Col>
            </Row>

            {/* Progress Bar */}
            <Progress
              percent={stat.percentages.taught}
              strokeColor="#52c41a"
              format={() => `${stat.taughtHours}/${stat.totalHours}`}
              size="small"
              style={{ marginTop: '8px' }}
            />
          </div>
        </Col>
      ))}
    </Row>
  </Spin>
</Card>

        {/* Search and Controls Section */}
        <Card style={{ marginBottom: '20px' }}>
          <Input
            placeholder={t('searchByClassCode')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: '400px', marginRight: '16px' }}
            prefix={<BookOutlined style={{ color: '#bfbfbf' }} />}
            allowClear
          />
          <Button type="default" onClick={handleRefresh} icon={<ReloadOutlined />}>
            {t('Refresh')}
          </Button>
        </Card>

        {/* Classes Table */}
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredClassGroups}
            rowKey="groupid"
            pagination={{
              current: currentPage,
              pageSize: itemsPerPage,
              total: filteredClassGroups.length,
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false,
            }}
            scroll={{ x: 'max-content' }}
            bordered
            style={{
              overflowX: 'auto',
              width: '100%',
              maxWidth: '100%'
            }}
          />
        </Spin>

        {/* Schedule Modal */}
        <ScheduleModal 
          isVisible={!!popupData} 
          onClose={() => setPopupData(null)} 
          popupData={popupData} 
          classChangeLogs={classChangeLogs} 
        />

        {/* Edit Modal */}
        <Modal
          title={<><EditOutlined /> {t('editClassGroup')}</>}
          open={editModalVisible}
          onCancel={() => {
              setEditModalVisible(false);
              form.resetFields();   // 🔥 reset values
            }}
          footer={[
            <Button 
              key="cancel" 
              onClick={() => {
                setEditModalVisible(false);
                form.resetFields();   // 🔥 reset values
              }}
            >
              {t('cancel')}
            </Button>,
            <Button key="submit" type="primary" onClick={handleSaveEdit}>
              {t('save')}
            </Button>
          ]}
          destroyOnClose
          maskClosable={false}
          width={600}
          centered
        >
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
          >
            <Form.Item
              name="class_code"
              label={<Space><BookOutlined />{t('classCode')}</Space>}
              rules={[
                { required: true, message: t('pleaseEnterClassCode') }
              ]}
            >
              <Input placeholder={t('enterClassCode')} />
            </Form.Item>
            
            <Form.Item
              name="book_details_materials"
              label={<Space><BookOutlined />{t('note')}</Space>}
            >
              <Input.TextArea 
                rows={3}
                placeholder={t('Note')} 
              />
            </Form.Item>
            
            <Divider />
            
            <Form.Item
              name="isprivate"
              label={<Space><UserOutlined />{t('Type')}</Space>}
              rules={[
                { required: false, message: t('pleaseSelectClassType') }
              ]}
            >
              <Select placeholder={t('selectClassType')}>
                <Option value="true">{t('private')}</Option>
                <Option value="false">{t('group')}</Option>
              </Select>
            </Form.Item>
          

            <Form.Item
              name="register_capacity"
              label={<Space><UserOutlined />{t('registerCapacity')}</Space>}
              rules={[
                { required: false, message: t('pleaseEnterCapacity') }
              ]}
            >
              <InputNumber 
                min={1} 
                placeholder={t('enterCapacity')}
                style={{ width: '100%' }}
              />
            </Form.Item>
            
            <Divider />
            
            <Form.Item
              name="price"
              label={<Space><DollarOutlined />{t('price')}</Space>}
              rules={[
                { required: true, message: t('pleaseEnterPrice') }
              ]}
              tooltip={editData?.hasRegistrations ? t('Cannot change price when students are registered') : null}
            >
              <InputNumber
                min={0}
                step={0.01}
                precision={2}
                prefix="฿"
                style={{ width: '100%' }}
                placeholder={t('enterPrice')}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                disabled={editData?.hasRegistrations}
              />
            </Form.Item>
<Form.Item
  name="salesperson_id"
  label={<Space><UserOutlined />{t('salesperson')}</Space>}
>
  <Select
    placeholder={t('selectSalesperson')}
    allowClear
    style={{ width: '100%' }}
    onChange={(value, option) => {
      // Only show confirmation if changing from an existing salesperson to a different one
      const currentValue = form.getFieldValue('salesperson_id');
      
      if (currentValue && currentValue !== value) {
        Modal.confirm({
          title: t('Confirm Salesperson Change'),
          content: t('Are you sure you want to change the salesperson for this class group?'),
          okText: t('yes'),
          cancelText: t('no'),
          onOk: () => {
            // Keep the new selection - form will handle this automatically
            console.log('✅ Salesperson changed to:', value);
          },
          onCancel: () => {
            // Revert to previous selection
            form.setFieldsValue({ salesperson_id: currentValue });
          }
        });
      }
    }}
  >
    {salespersons.map(sp => (
      <Option key={sp.id} value={sp.id}>
        {sp.full_name} {sp.nickname ? `(${sp.nickname})` : ''} - {sp.email}
      </Option>
    ))}
  </Select>
</Form.Item>

                    <Form.Item hidden name="groupID">
                      <Input />
                    </Form.Item>
                  </Form>
                </Modal>
              </div>
            </div>
          );
        };

export default withRoleAccess(FilterTaskTable, ['admin', 'superadmin']);